'use client';

import { GetResponseType } from '@/api/types/api';
import { ApiResponseDefinition } from '@/api/types/api-definition';
import MagicJump from '@/components/MagicJump';
import { useReporting } from '@/hooks/useReporting';
import { useRouteMetadata } from '@/hooks/useRouteMetadata';
import { useStore } from '@/providers/app-store-provider';
import { ProviderType } from '@/reporting';
import logger from '@/utils/client-logger';
import { setSession } from '@/utils/tools';
import { Drawer } from '@mui/material';
import { CloseOutlined } from '@zeelool/icon';
import { useTranslations } from 'next-intl';
import Image from '@/components/Image';
import Link from 'next/link';
import { useMemo } from 'react';
import MenuItemTablet from './MenuItem.tablet';

export interface SideNavTabletProps {
  show: boolean;
  navData: GetResponseType<ApiResponseDefinition['GET:/api/nav/lists']>;
  quickEntryData?: Record<string, any>;
  onClose: () => void;
}

// 平板设备的侧边导航栏
const SideNavTablet = ({ show, navData, quickEntryData, onClose }: SideNavTabletProps) => {
  const { trackEvent } = useReporting();
  const { cnName } = useRouteMetadata();
  const blindMode = useStore((state) => state.waiMode);
  const setBlindMode = useStore((state) => state.setWaiMode);

  const t = useTranslations('layout.header');

  // 过滤掉只有图片导航的一级菜单 - 转换Vue的computed逻辑
  const filterNavData = useMemo(() => {
    let result = [];
    try {
      result =
        navData?.filter(
          (nav) => !nav.children?.every((child) => [3].includes(child.template_id || 0)),
        ) || [];
    } catch (error) {
      logger.error('Filter navigation data error:', error);
    }
    return result;
  }, [navData]);

  // 处理关闭导航
  const handleClose = () => {
    onClose();
  };

  return (
    <Drawer
      open={show}
      onClose={handleClose}
      anchor="left"
      slotProps={{
        paper: {
          style: {
            width: '420px',
            maxWidth: '100vw',
          },
        },
      }}
      keepMounted
    >
      {/* Header */}
      <div className="flex items-center border-b border-[#eee] mb-0 pt-[46px] pr-[24px] pb-[20px] pl-[32px]">
        <CloseOutlined className="cursor-pointer text-[24px]" onClick={handleClose} />
        <Link prefetch={false} href="/" className="ml-[20px]">
          <Image
            src="https://s3.zeelool.com/admin/shop/home/688a0c469cf861c1e040ba5e7873e26e.png"
            alt={t('logo')}
            width={158}
            height={40}
            className="cursor-pointer"
          />
        </Link>
      </div>

      {/* Navigation Content */}
      <div className="flex-1 overflow-auto relative h-full pl-[32px] pr-[24px] pb-[100px]">
        {filterNavData &&
          filterNavData.length > 0 &&
          filterNavData.map((item, index) => (
            <MenuItemTablet key={item.id} item={item} tabIndex={index} onCloseNav={handleClose} />
          ))}
      </div>

      {/* Footer - HeaderNoticeMin 组件重构 */}
      <div className="absolute z-[2] bottom-0 left-0 right-0 flex justify-between items-center w-full h-[40px] box-border bg-[#f4f4f4] pl-[32px] pr-[24px]">
        <div className="flex justify-center items-center h-full transition-all duration-[300ms]">
          {/* 快捷入口链接 */}
          {quickEntryData?.details?.map((item, index) => {
            return (
              <MagicJump
                key={`${index}a`}
                item={{
                  jump_type: item.jump_type,
                  Jump_target: item.Jump_target,
                }}
                aria-label={item.quick_title}
                onBeforeJump={() => {
                  setSession('zl_goodsListEnterPageScene', '全局导航');
                  trackEvent(
                    {
                      eventName: 'TopNavTab_Click',
                      properties: {
                        page_name: cnName,
                        url_path: window.location.href,
                        tab_name: item.quick_title,
                        tab_index: '',
                      },
                    },
                    [ProviderType.SENSORS],
                  );
                  onClose();
                }}
                className={`text-[12px] leading-[16px] text-[#282828] cursor-pointer no-underline ${
                  index < (quickEntryData?.details?.length || 0) - 1 ? 'mr-[32px]' : ''
                }`}
              >
                <span>{item.quick_title}</span>
              </MagicJump>
            );
          })}

          {/* 无障碍模式切换 */}
          <span
            onClick={() => {
              setBlindMode(!blindMode);
              trackEvent(
                {
                  eventName: 'TopNavTab_Click',
                  properties: {
                    page_name: cnName,
                    url_path: window.location.href,
                    tab_name: `Accessibility_${!blindMode ? 'Open' : 'Close'}`,
                    first_level_category: '',
                  },
                },
                [ProviderType.SENSORS],
              );
            }}
            className={`text-[12px] leading-[16px] text-primary-text cursor-pointer ${
              quickEntryData?.details?.length ? 'ml-[32px]' : ''
            }`}
          >
            {blindMode ? t('disableAccessibility') : t('topBar.enableAccessibility')}
          </span>
        </div>
      </div>
    </Drawer>
  );
};

export default SideNavTablet;
